import React from "react";
import ReactApexChart from "react-apexcharts";
import { Typography } from "antd";

const { Text } = Typography;

interface Bucket {
  label: string;
  low: number;
  high: number;
  count: number;
  isTopBucket?: boolean;
}

interface ValuationChartProps {
  sectorPE: number;
  companyPE: number;
  companyRank: number;
  totalStocks: number;
  peDeviation: number;
  buckets: Bucket[];
  label?: string;
  sectorLabel?: string;
  isRed?: boolean;
  isPercentage?: boolean;
}

export const ValuationChart: React.FC<ValuationChartProps> = ({
  sectorPE,
  companyPE,
  companyRank,
  totalStocks,
  peDeviation,
  buckets,
  label = "P / E",
  sectorLabel = "SECTOR P/E",
  isRed = false,
  isPercentage = false,
}) => {
  const isActive = (b: Bucket) =>
    b.isTopBucket
      ? companyPE >= b.low && companyPE <= b.high
      : companyPE >= b.low && companyPE < b.high;

  // Semantic red/green — intentionally literal (not the accent/chart
  // palette), matching the same precedent used for the EPS sparkline
  // and Peers table deltas.
  const activeColor = isRed ? "#ef4444" : "#22c55e";

  // The inactive-bucket fill needs to read as "muted" against the card
  // surface in BOTH themes — a flat light gray reads as recessive on
  // white but pops brightly on the dark navy card, inverting the
  // intended active/inactive hierarchy. --vi-text-tertiary carries the
  // same "muted" contrast relationship to --vi-card-bg in either mode.
  const colors = buckets.map((b) =>
    isActive(b) ? activeColor : "var(--vi-text-tertiary)",
  );

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: true },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 3,
        distributed: true,
        dataLabels: { position: "bottom" },
      },
    },
    colors,
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: buckets.map((b) => b.label),
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: { style: { fontSize: "11px", colors: "var(--vi-text-tertiary)" } },
    },
    grid: {
      borderColor: "var(--vi-card-border)",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    tooltip: {
      y: { formatter: (val) => `${val}` },
    },
  };

  const series = [{ name: "Companies", data: buckets.map((b) => b.count) }];

  const deviationPositive = peDeviation >= 0;
  const ordinalSuffix = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  return (
    <div className="vi-glass-card p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-1">
        <Text strong className="text-sm text-gray-600">
          {label}
        </Text>
        <div className="text-right">
          <Text className="text-xs text-gray-400 block">{sectorLabel}</Text>
          <Text strong className="text-lg">
            {sectorPE.toFixed(2)}
            {isPercentage ? "%" : ""}
          </Text>
        </div>
      </div>

      {/* Chart */}
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={220}
      />

      {/* Footer */}
      <div className="border-t border-gray-100 mt-1 pt-3 flex justify-between items-center">
        <div>
          <Text className="text-xs text-gray-400 block">COMPANY</Text>
          <Text strong className="text-xl">
            {companyPE.toFixed(2)}
            {isPercentage ? "%" : ""}
          </Text>
        </div>
        <div>
          <div
            className="px-3 py-1 rounded-md text-sm font-semibold"
            style={{
              // A flat pastel chip (#dcfce7/#fee2e2) assumes a white
              // surface behind it — on the dark card it would sit as a
              // bright, out-of-place patch. A translucent tint of the
              // same literal green/red over the surface reads correctly
              // in both themes instead.
              backgroundColor: deviationPositive
                ? "rgba(22, 163, 74, 0.16)"
                : "rgba(220, 38, 38, 0.16)",
              color: deviationPositive ? "#16a34a" : "#dc2626",
            }}
          >
            {deviationPositive ? "+" : ""}
            {peDeviation.toFixed(2)}
          </div>
          <Text className="text-xs text-gray-400 block">
            {companyRank}
            {ordinalSuffix(companyRank)} out of {totalStocks}
          </Text>
        </div>
      </div>
    </div>
  );
};
